import { useState, useMemo, useEffect } from 'react'
import {
  initialNeedsReview,
  initialUnmatchedSF,
  initialUnmatchedStripe,
  initialMatched,
  suggestionsMap,
} from '../data/mockData'
import LoadingSpinner from './common/LoadingSpinner'
import ErrorState from './common/ErrorState'
import Pagination from './common/Pagination'
import ConfirmationModal from './common/ConfirmationModal'
import { showSuccess, showError } from '../utils/toast'
import { PAGINATION_DEFAULTS } from '../config/api.config'

// ── Shared icons ───────────────────────────────────────────────────────────
function CheckIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
}
function XIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
}
function SearchIcon() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
}
function LinkIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
}
function UnlinkIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.84 12.25l1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71"/><path d="M5.17 11.75l-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71"/><line x1="8" y1="2" x2="8" y2="5"/><line x1="2" y1="8" x2="5" y2="8"/><line x1="16" y1="19" x2="16" y2="22"/><line x1="19" y1="16" x2="22" y2="16"/></svg>
}
function BuildingIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
}
function AlertIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
}
function ClockIcon() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
}
function SortIcon({ active, dir }) {
  if (!active) return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-30 flex-shrink-0">
      <path d="M8 6l4-4 4 4M8 18l4 4 4-4"/>
    </svg>
  )
  return dir === 'asc' ? (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
      <path d="M12 19V5M5 12l7-7 7 7"/>
    </svg>
  ) : (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
      <path d="M12 5v14M19 12l-7 7-7-7"/>
    </svg>
  )
}

// ── Method badge ───────────────────────────────────────────────────────────
function MethodBadge({ method }) {
  const config = {
    auto:         { label: 'Auto',         cls: 'bg-blue-50 text-blue-700 border border-blue-100' },
    admin_manual: { label: 'Admin Manual', cls: 'bg-purple-50 text-purple-700 border border-purple-100' },
    agent_manual: { label: 'Agent Manual', cls: 'bg-amber-50 text-amber-700 border border-amber-100' },
  }
  const { label, cls } = config[method] || { label: method, cls: 'bg-gray-100 text-gray-600' }
  return <span className={`badge text-xs ${cls}`}>{label}</span>
}

// ── Confidence pill ────────────────────────────────────────────────────────
function ConfidencePill({ confidence }) {
  const color = confidence >= 95 ? 'bg-green-100 text-green-700' : confidence >= 85 ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'
  return <span className={`badge text-xs ${color}`}>{confidence}% match</span>
}

// ── Salesforce side card ───────────────────────────────────────────────────
function SFCard({ account }) {
  return (
    <div className="flex-1 p-4 rounded-xl border border-blue-100 bg-blue-50 space-y-3 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
          <BuildingIcon />
        </div>
        <span className="text-xs font-semibold text-blue-500 uppercase tracking-wide">Salesforce</span>
        {account.isPersonalAccount && (
          <span className="badge bg-blue-100 text-blue-600 text-xs">Personal</span>
        )}
      </div>
      <div>
        <div className="font-semibold text-gray-900 text-sm">{account.name}</div>
        {account.website && (
          <div className="text-xs text-gray-500 mt-0.5 font-mono">{account.website}</div>
        )}
      </div>
      <div className="space-y-1.5 pt-1 border-t border-blue-100">
        <div className="flex items-start gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-700 truncate">{account.primaryContact.name}</div>
            <div className="text-xs text-gray-400 truncate">{account.primaryContact.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">{account.contactCount} contact{account.contactCount !== 1 ? 's' : ''}</span>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-400">{account.industry}</span>
        </div>
      </div>
    </div>
  )
}

// ── Stripe side card ───────────────────────────────────────────────────────
function StripeCard({ customer }) {
  return (
    <div className="flex-1 p-4 rounded-xl border border-stripe-purple/20 bg-stripe-purple-light space-y-3 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-6 h-6 rounded bg-stripe-purple/10 flex items-center justify-center flex-shrink-0">
          <svg width="12" height="12" viewBox="0 0 32 32" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M15.45 11.6c0-.93.76-1.29 2.02-1.29 1.8 0 4.09.55 5.89 1.52V6.43C21.47 5.53 19.08 5 15.47 5 10.3 5 6.8 7.62 6.8 12.04c0 6.89 9.49 5.79 9.49 8.77 0 1.1-.96 1.46-2.29 1.46-1.98 0-4.52-.82-6.52-1.93v5.47c2.22.96 4.47 1.36 6.52 1.36 4.96 0 8.73-2.46 8.73-6.96-.03-7.44-9.28-6.13-9.28-8.61z" fill="#635BFF"/>
          </svg>
        </div>
        <span className="text-xs font-semibold text-stripe-purple uppercase tracking-wide">Stripe</span>
      </div>
      <div>
        <div className="font-semibold text-gray-900 text-sm">{customer.name}</div>
        <div className="text-xs text-gray-500 mt-0.5 truncate">{customer.email}</div>
      </div>
      <div className="space-y-1.5 pt-1 border-t border-stripe-purple/10">
        <div className="flex items-center gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <span className="text-xs text-gray-400">Since {customer.created}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          <span className="text-xs font-medium text-gray-700">{customer.totalVolume}</span>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-400">{customer.transactions} txns</span>
        </div>
        {customer.lastTransaction && (
          <div className="flex items-center gap-1.5">
            <ClockIcon />
            <span className="text-xs text-gray-400">Last txn {customer.lastTransaction}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
          <span className="text-xs text-gray-400">{customer.paymentMethod}</span>
        </div>
      </div>
    </div>
  )
}

// ── Multi-match card (for Needs Review) ───────────────────────────────────
function MultiMatchCard({ match, onApprove, onReject }) {
  const [selectedIdx, setSelectedIdx] = useState(0)

  const selectedCandidate = match.candidates[selectedIdx]

  const handleApprove = () => {
    onApprove(match.id, selectedCandidate.stripeCustomer, selectedCandidate.matchReason)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row items-stretch gap-3">
        <SFCard account={match.sfAccount} />

        {/* Center connector */}
        <div className="flex lg:flex-col items-center justify-center px-2 py-2 lg:py-0 gap-2 lg:gap-1">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="rotate-90 lg:rotate-0">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
          <span className="badge bg-orange-100 text-orange-700 text-xs whitespace-nowrap">
            {match.candidates.length} matches
          </span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="rotate-90 lg:rotate-0">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </div>

        {/* Candidates list */}
        <div className="flex-1 rounded-xl border border-orange-200 overflow-hidden min-w-0">
          <div className="px-3 py-2 bg-orange-50 border-b border-orange-100 flex items-center gap-2">
            <span className="text-orange-600"><AlertIcon /></span>
            <span className="text-xs font-semibold text-orange-700">
              {match.candidates.length} Stripe customers found — select the correct one
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {match.candidates.map((candidate, idx) => (
              <label
                key={idx}
                className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${
                  selectedIdx === idx ? 'bg-white' : 'hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name={`multi-${match.id}`}
                  checked={selectedIdx === idx}
                  onChange={() => setSelectedIdx(idx)}
                  className="mt-1 h-3.5 w-3.5 accent-indigo-600 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-gray-900">{candidate.stripeCustomer.name}</span>
                    <ConfidencePill confidence={candidate.confidence} />
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">{candidate.stripeCustomer.email}</div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-400 flex-wrap">
                    <span>{candidate.stripeCustomer.totalVolume} · {candidate.stripeCustomer.transactions} txns</span>
                    <span>·</span>
                    <span>Last txn {candidate.stripeCustomer.lastTransaction}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-gray-400"><LinkIcon /></span>
                    <span className="text-xs text-gray-500">{candidate.matchReason}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-gray-400"><LinkIcon /></span>
          <span className="text-sm text-gray-600">
            <span className="font-medium">Multiple matches found</span> — select the correct Stripe customer above
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => onReject(match.id)} className="btn-danger">
            <XIcon />
            Reject All
          </button>
          <button onClick={handleApprove} className="btn-success">
            <CheckIcon />
            Approve Selected
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Single match card (for Needs Review) ──────────────────────────────────
function SingleMatchCard({ match, isSelected, onToggleSelect, onApprove, onReject }) {
  return (
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggleSelect}
        className="mt-1 h-4 w-4 rounded border-gray-300 accent-blue-600 cursor-pointer flex-shrink-0"
      />
      <div className="flex-1 space-y-4">
        {/* Match pair */}
        <div className="flex flex-col lg:flex-row items-stretch gap-3">
          <SFCard account={match.sfAccount} />
          <div className="flex lg:flex-col items-center justify-center gap-2 lg:gap-1 px-2 py-2 lg:py-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="rotate-90 lg:rotate-0">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
            <ConfidencePill confidence={match.confidence} />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="rotate-90 lg:rotate-0">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
          <StripeCard customer={match.stripeCustomer} />
        </div>

        {/* Match reason + actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <LinkIcon />
            <span className="text-sm text-gray-600">
              <span className="font-medium">Match reason: </span>
              {match.matchReason}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => onReject(match.id)} className="btn-danger">
              <XIcon />
              Reject
            </button>
            <button onClick={() => onApprove(match.id)} className="btn-success">
              <CheckIcon />
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Tab 1: Needs Review ────────────────────────────────────────────────────
const parseVol = v => parseInt((v || '$0').replace(/[$,]/g, ''), 10)

function NeedsReviewTab({ matches, onApprove, onReject, onBulkApprove, onBulkReject }) {
  const [dismissed, setDismissed] = useState(new Set())
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [sortBy, setSortBy] = useState('confidence-desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(PAGINATION_DEFAULTS.PER_PAGE)

  const visible = matches.filter(m => !dismissed.has(m.id))
  const singleVisible = visible.filter(m => !m.isMultiMatch)
  const allSingleSelected = singleVisible.length > 0 && singleVisible.every(m => selectedIds.has(m.id))
  const selectedCount = singleVisible.filter(m => selectedIds.has(m.id)).length

  const sorted = useMemo(() => {
    const arr = [...visible]
    switch (sortBy) {
      case 'confidence-asc':
        return arr.sort((a, b) => a.confidence - b.confidence)
      case 'volume-desc':
        return arr.sort((a, b) => {
          const va = a.isMultiMatch ? parseVol(a.candidates[0]?.stripeCustomer?.totalVolume) : parseVol(a.stripeCustomer?.totalVolume)
          const vb = b.isMultiMatch ? parseVol(b.candidates[0]?.stripeCustomer?.totalVolume) : parseVol(b.stripeCustomer?.totalVolume)
          return vb - va
        })
      case 'name-asc':
        return arr.sort((a, b) => a.sfAccount.name.localeCompare(b.sfAccount.name))
      default: // confidence-desc
        return arr.sort((a, b) => b.confidence - a.confidence)
    }
  }, [visible, sortBy])

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage))
  const pageStart = (currentPage - 1) * perPage
  const pageItems = sorted.slice(pageStart, pageStart + perPage)

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const dismiss = (id) => setDismissed(prev => new Set([...prev, id]))
  const deselect = (id) => setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s })

  const handleApprove = (id, sc = null, reason = null) => {
    dismiss(id)
    deselect(id)
    onApprove(id, sc, reason)
  }
  const handleReject = (id) => {
    onReject(id) // Parent may show confirmation modal; list updates when parent state changes
  }
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev)
      if (s.has(id)) s.delete(id)
      else s.add(id)
      return s
    })
  }
  const toggleAll = () => {
    if (allSingleSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(singleVisible.map(m => m.id)))
    }
  }
  const handleBulkApprove = () => {
    const ids = singleVisible.filter(m => selectedIds.has(m.id)).map(m => m.id)
    ids.forEach(id => dismiss(id))
    setSelectedIds(new Set())
    onBulkApprove(ids)
  }
  const handleBulkReject = () => {
    const ids = singleVisible.filter(m => selectedIds.has(m.id)).map(m => m.id)
    if (ids.length === 0) return
    onBulkReject(ids) // Parent may show confirmation modal
  }
  const handleApproveAll = () => {
    const ids = singleVisible.map(m => m.id)
    ids.forEach(id => dismiss(id))
    setSelectedIds(new Set())
    onBulkApprove(ids)
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {singleVisible.length > 0 && (
            <input
              type="checkbox"
              checked={allSingleSelected}
              onChange={toggleAll}
              className="h-4 w-4 rounded border-gray-300 accent-blue-600 cursor-pointer"
            />
          )}
          <p className="text-sm text-gray-500">
            {visible.length === 0
              ? 'All reviewed — nothing left to action.'
              : `${visible.length} pair${visible.length !== 1 ? 's' : ''} need your review`}
          </p>
          {visible.length > 1 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">Sort:</span>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 cursor-pointer"
              >
                <option value="confidence-desc">Confidence: High → Low</option>
                <option value="confidence-asc">Confidence: Low → High</option>
                <option value="volume-desc">Stripe Volume: High → Low</option>
                <option value="name-asc">Account Name: A → Z</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {selectedCount > 0 ? (
            <>
              <button onClick={handleBulkReject} className="btn-danger">
                <XIcon />
                Reject Selected ({selectedCount})
              </button>
              <button onClick={handleBulkApprove} className="btn-success">
                <CheckIcon />
                Approve Selected ({selectedCount})
              </button>
            </>
          ) : singleVisible.length > 0 ? (
            <button onClick={handleApproveAll} className="btn-success">
              <CheckIcon />
              Approve All ({singleVisible.length})
            </button>
          ) : null}
        </div>
      </div>

      {/* Match cards */}
      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div className="text-gray-700 font-medium">All done!</div>
          <div className="text-gray-400 text-sm mt-1">No more matches to review.</div>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {pageItems.map(match => (
              <div key={match.id} className={`card p-5 space-y-4 transition-colors ${!match.isMultiMatch && selectedIds.has(match.id) ? 'ring-2 ring-blue-200' : ''}`}>
                {match.isMultiMatch ? (
                  <MultiMatchCard
                    match={match}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                ) : (
                  <SingleMatchCard
                    match={match}
                    isSelected={selectedIds.has(match.id)}
                    onToggleSelect={() => toggleSelection(match.id)}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                )}
              </div>
            ))}
          </div>
          {sorted.length > perPage && (
            <div className="mt-4 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                perPage={perPage}
                totalItems={sorted.length}
                onPageChange={setCurrentPage}
                onPerPageChange={(v) => { setPerPage(v); setCurrentPage(1) }}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Tab 2: Unmatched ───────────────────────────────────────────────────────
function UnmatchedTab({ sfAccounts, stripeCustomers, onLink }) {
  const [selectedSF, setSelectedSF] = useState(null)
  const [search, setSearch] = useState('')
  const [linkedPairs, setLinkedPairs] = useState([])
  const [flash, setFlash] = useState(null)

  const activeSF = sfAccounts.filter(a => !linkedPairs.some(p => p.sfId === a.id))
  const activeStripe = stripeCustomers.filter(c => !linkedPairs.some(p => p.stripeId === c.id))

  const suggestions = useMemo(() => {
    if (!selectedSF) return []
    const sugIds = suggestionsMap[selectedSF] || []
    return activeStripe.filter(c => sugIds.includes(c.id))
  }, [selectedSF, activeStripe])

  const filteredStripe = useMemo(() => {
    const q = search.toLowerCase()
    const base = activeStripe.filter(c => {
      const isSuggestion = suggestions.some(s => s.id === c.id)
      return !isSuggestion
    })
    if (!q) return base
    return base.filter(c => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
  }, [search, activeStripe, suggestions])

  const handleLink = (stripeCustomer) => {
    if (!selectedSF) return
    const sfAccount = sfAccounts.find(a => a.id === selectedSF)
    setLinkedPairs(prev => [...prev, { sfId: selectedSF, stripeId: stripeCustomer.id }])
    setFlash(`Linked ${sfAccount.name} → ${stripeCustomer.name}`)
    setSelectedSF(null)
    onLink(selectedSF, stripeCustomer.id, sfAccount.name, stripeCustomer.name, stripeCustomer.email)
    setTimeout(() => setFlash(null), 3000)
  }

  return (
    <div className="space-y-4">
      {flash && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-100 rounded-lg">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          <span className="text-sm text-green-700 font-medium">{flash}</span>
        </div>
      )}

      <p className="text-sm text-gray-500">
        {selectedSF
          ? 'Now click a Stripe customer on the right to link it.'
          : 'Click a Salesforce account to see suggested Stripe matches.'}
      </p>

      <div className="grid grid-cols-2 gap-4" style={{ minHeight: 480 }}>
        {/* Left: SF Accounts */}
        <div className="card overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 bg-blue-50 flex items-center gap-2">
            <BuildingIcon />
            <span className="text-sm font-semibold text-blue-700">Salesforce Accounts</span>
            <span className="badge bg-blue-100 text-blue-600 ml-auto">{activeSF.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {activeSF.length === 0 ? (
              <div className="flex items-center justify-center h-full py-12 text-center px-4">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">All accounts linked!</div>
                  <div className="text-xs text-gray-400">Nothing left to match here.</div>
                </div>
              </div>
            ) : (
              activeSF.map(account => (
                <button
                  key={account.id}
                  onClick={() => setSelectedSF(account.id === selectedSF ? null : account.id)}
                  className={`w-full text-left p-3.5 transition-all ${
                    selectedSF === account.id
                      ? 'bg-blue-50 border-l-2 border-blue-500'
                      : 'hover:bg-gray-50 border-l-2 border-transparent'
                  }`}
                >
                  <div className="font-medium text-sm text-gray-900">{account.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5 font-mono">{account.website}</div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">{account.primaryContact.email}</div>
                  <div className="mt-1.5">
                    <span className="badge bg-gray-100 text-gray-500 text-xs">{account.industry}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: Stripe Customers */}
        <div className="card overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 bg-stripe-purple-light flex items-center gap-2">
            <svg width="13" height="13" viewBox="0 0 32 32" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M15.45 11.6c0-.93.76-1.29 2.02-1.29 1.8 0 4.09.55 5.89 1.52V6.43C21.47 5.53 19.08 5 15.47 5 10.3 5 6.8 7.62 6.8 12.04c0 6.89 9.49 5.79 9.49 8.77 0 1.1-.96 1.46-2.29 1.46-1.98 0-4.52-.82-6.52-1.93v5.47c2.22.96 4.47 1.36 6.52 1.36 4.96 0 8.73-2.46 8.73-6.96-.03-7.44-9.28-6.13-9.28-8.61z" fill="#635BFF"/></svg>
            <span className="text-sm font-semibold text-stripe-purple">Stripe Customers</span>
            <span className="badge bg-stripe-purple/10 text-stripe-purple ml-auto">{activeStripe.length}</span>
          </div>

          {/* Search */}
          <div className="px-3 py-2.5 border-b border-gray-100">
            <div className="relative">
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stripe-purple/30 focus:border-stripe-purple/50"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {/* Suggestions */}
            {suggestions.length > 0 && (
              <>
                <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-100 flex items-center gap-1.5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <span className="text-xs font-semibold text-amber-700">Suggested matches</span>
                </div>
                {suggestions.map(customer => (
                  <button
                    key={customer.id}
                    onClick={() => handleLink(customer)}
                    disabled={!selectedSF}
                    className={`w-full text-left p-3.5 border-l-2 border-amber-300 bg-amber-50/50 transition-all ${
                      selectedSF ? 'hover:bg-amber-100 cursor-pointer' : 'opacity-60 cursor-default'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm text-gray-900">{customer.name}</div>
                      {selectedSF && <span className="text-xs text-stripe-purple font-medium flex items-center gap-1"><LinkIcon /> Link</span>}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 truncate">{customer.email}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{customer.totalVolume} · since {customer.created}</div>
                  </button>
                ))}
                <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-400">All customers</span>
                </div>
              </>
            )}

            {/* All customers */}
            {filteredStripe.length === 0 && suggestions.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-center px-4">
                <div className="text-sm text-gray-400">
                  {search ? `No customers matching "${search}"` : 'No unmatched customers'}
                </div>
              </div>
            ) : (
              filteredStripe.map(customer => (
                <button
                  key={customer.id}
                  onClick={() => handleLink(customer)}
                  disabled={!selectedSF}
                  className={`w-full text-left p-3.5 border-l-2 border-transparent transition-all ${
                    selectedSF ? 'hover:bg-stripe-purple-light hover:border-stripe-purple cursor-pointer' : 'opacity-60 cursor-default'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm text-gray-900">{customer.name}</div>
                    {selectedSF && <span className="text-xs text-stripe-purple font-medium flex items-center gap-1"><LinkIcon /> Link</span>}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">{customer.email}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{customer.totalVolume} · since {customer.created}</div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Tab 3: Matched ─────────────────────────────────────────────────────────
function MatchedTab({ matches, onUnlink, onBulkUnlink }) {
  const [unlinked, setUnlinked] = useState(new Set())
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [flash, setFlash] = useState(null)
  const [filterMethod, setFilterMethod] = useState('all')
  const [search, setSearch] = useState('')
  const [sortCol, setSortCol] = useState('date')
  const [sortDir, setSortDir] = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(PAGINATION_DEFAULTS.PER_PAGE)

  const visible = matches.filter(m => !unlinked.has(m.id))

  const filtered = useMemo(() => {
    return visible.filter(m => {
      if (filterMethod !== 'all' && m.method !== filterMethod) return false
      if (search) {
        const q = search.toLowerCase()
        return m.sfName.toLowerCase().includes(q) || m.stripeName.toLowerCase().includes(q)
      }
      return true
    })
  }, [visible, filterMethod, search])

  const sorted = useMemo(() => {
    const arr = [...filtered]
    return arr.sort((a, b) => {
      let va, vb
      if (sortCol === 'sf')     { va = a.sfName;                    vb = b.sfName }
      if (sortCol === 'stripe') { va = a.stripeName;                vb = b.stripeName }
      if (sortCol === 'method') { va = a.method;                    vb = b.method }
      if (sortCol === 'date')   { va = new Date(a.matchedDate);     vb = new Date(b.matchedDate) }
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [filtered, sortCol, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage))
  const pageStart = (currentPage - 1) * perPage
  const pageItems = sorted.slice(pageStart, pageStart + perPage)

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const handleSortCol = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const allSelected = filtered.length > 0 && filtered.every(m => selectedIds.has(m.id))
  const selectedCount = filtered.filter(m => selectedIds.has(m.id)).length

  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev)
      if (s.has(id)) s.delete(id)
      else s.add(id)
      return s
    })
  }
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filtered.map(m => m.id)))
    }
  }

  const handleUnlink = (id, sfName) => {
    setUnlinked(prev => new Set([...prev, id]))
    setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s })
    setFlash(`Unlinked ${sfName}`)
    onUnlink(id)
    setTimeout(() => setFlash(null), 3000)
  }

  const handleBulkUnlink = () => {
    const toUnlink = filtered.filter(m => selectedIds.has(m.id))
    const ids = toUnlink.map(m => m.id)
    const count = ids.length
    ids.forEach(id => setUnlinked(prev => new Set([...prev, id])))
    setSelectedIds(new Set())
    setFlash(`Unlinked ${count} match${count !== 1 ? 'es' : ''}`)
    onBulkUnlink(ids)
    setTimeout(() => setFlash(null), 3000)
  }

  const methodFilters = [
    { id: 'all',          label: 'All' },
    { id: 'auto',         label: 'Auto' },
    { id: 'admin_manual', label: 'Admin Manual' },
    { id: 'agent_manual', label: 'Agent Manual' },
  ]

  return (
    <div className="space-y-4">
      {flash && (
        <div className="flex items-center gap-2 px-4 py-3 bg-orange-50 border border-orange-100 rounded-lg">
          <UnlinkIcon />
          <span className="text-sm text-orange-700 font-medium">{flash}</span>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search accounts..."
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setCurrentPage(1)
              }}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg w-48 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
            />
          </div>
          {/* Method filter tabs */}
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-1">
            {methodFilters.map(f => (
              <button
                key={f.id}
                onClick={() => {
                  setFilterMethod(f.id)
                  setCurrentPage(1)
                }}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  filterMethod === f.id
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</p>
          {selectedCount > 0 && (
            <button
              onClick={handleBulkUnlink}
              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1.5 border border-red-200 rounded-lg px-3 py-1.5 transition-colors hover:bg-red-50"
            >
              <UnlinkIcon />
              Unlink Selected ({selectedCount})
            </button>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={filtered.length === 0}
                  className="h-4 w-4 rounded border-gray-300 accent-blue-600 cursor-pointer disabled:opacity-40"
                />
              </th>
              {[
                { col: 'sf',     label: 'Salesforce Account' },
                { col: 'stripe', label: 'Stripe Customer' },
                { col: 'method', label: 'Method' },
                { col: 'date',   label: 'Matched' },
              ].map(({ col, label }) => (
                <th
                  key={col}
                  onClick={() => handleSortCol(col)}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                >
                  <div className="flex items-center gap-1.5">
                    {label}
                    <SortIcon active={sortCol === col} dir={sortDir} />
                  </div>
                </th>
              ))}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">
                  {search || filterMethod !== 'all'
                    ? 'No matches for current filters.'
                    : 'No confirmed matches yet. Approve from "Needs Review" or link from "Unmatched".'}
                </td>
              </tr>
            ) : (
              pageItems.map(match => (
                <tr
                  key={match.id}
                  className={`transition-colors ${
                    selectedIds.has(match.id) ? 'bg-blue-50/60' : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-3.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(match.id)}
                      onChange={() => toggleSelection(match.id)}
                      className="h-4 w-4 rounded border-gray-300 accent-blue-600 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="font-medium text-gray-900">{match.sfName}</div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-start gap-1.5">
                      <svg width="12" height="12" viewBox="0 0 32 32" fill="none" className="mt-0.5 flex-shrink-0"><path fillRule="evenodd" clipRule="evenodd" d="M15.45 11.6c0-.93.76-1.29 2.02-1.29 1.8 0 4.09.55 5.89 1.52V6.43C21.47 5.53 19.08 5 15.47 5 10.3 5 6.8 7.62 6.8 12.04c0 6.89 9.49 5.79 9.49 8.77 0 1.1-.96 1.46-2.29 1.46-1.98 0-4.52-.82-6.52-1.93v5.47c2.22.96 4.47 1.36 6.52 1.36 4.96 0 8.73-2.46 8.73-6.96-.03-7.44-9.28-6.13-9.28-8.61z" fill="#635BFF"/></svg>
                      <div>
                        <div className="text-gray-700">{match.stripeName}</div>
                        {match.stripeEmail && (
                          <div className="text-xs text-gray-400 mt-0.5">{match.stripeEmail}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <MethodBadge method={match.method} />
                    {match.matchReason && (
                      <div className="text-xs text-gray-400 mt-1">{match.matchReason}</div>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-gray-400 text-xs">{match.matchedDate}</td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() => handleUnlink(match.id, match.sfName)}
                      className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 ml-auto transition-colors"
                    >
                      <UnlinkIcon />
                      Unlink
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {sorted.length > perPage && (
        <div className="border-t border-gray-200">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            totalItems={sorted.length}
            onPageChange={setCurrentPage}
            onPerPageChange={(v) => { setPerPage(v); setCurrentPage(1) }}
          />
        </div>
      )}
    </div>
  )
}

// ── Matching Interface (top-level) ─────────────────────────────────────────
export default function MatchingInterface() {
  const [activeTab, setActiveTab] = useState('needs-review')
  const [needsReview, setNeedsReview] = useState(initialNeedsReview)
  const [unmatchedSF] = useState(initialUnmatchedSF)
  const [unmatchedStripe] = useState(initialUnmatchedStripe)
  const [matched, setMatched] = useState(initialMatched)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectTarget, setRejectTarget] = useState(null) // { single: id } | { bulk: ids }

  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  const handleApprove = (id, selectedStripe = null, selectedReason = null) => {
    try {
      const match = needsReview.find((m) => m.id === id)
      if (!match) return
      const sc = selectedStripe || match.stripeCustomer
      const reason = selectedReason || match.matchReason
      setMatched((prev) => [
        ...prev,
        {
          id: `approved-${id}`,
          sfName: match.sfAccount.name,
          stripeName: sc.name,
          stripeEmail: sc.email,
          method: 'auto',
          matchReason: reason,
          matchedDate: today,
        },
      ])
      setNeedsReview((prev) => prev.filter((m) => m.id !== id))
      showSuccess('Match approved')
    } catch (err) {
      console.error('Approve failed:', err)
      showError(err?.message ?? 'Failed to approve match')
    }
  }

  const requestReject = (id) => {
    setRejectTarget({ single: id })
    setRejectModalOpen(true)
  }

  const requestBulkReject = (ids) => {
    setRejectTarget({ bulk: ids })
    setRejectModalOpen(true)
  }

  const confirmReject = () => {
    try {
      if (rejectTarget?.single) {
        setNeedsReview((prev) => prev.filter((m) => m.id !== rejectTarget.single))
        showSuccess('Match rejected')
      } else if (rejectTarget?.bulk?.length) {
        setNeedsReview((prev) => prev.filter((m) => !rejectTarget.bulk.includes(m.id)))
        showSuccess(`${rejectTarget.bulk.length} match(es) rejected`)
      }
    } catch (err) {
      console.error('Reject failed:', err)
      showError(err?.message ?? 'Failed to reject')
    } finally {
      setRejectModalOpen(false)
      setRejectTarget(null)
    }
  }

  const handleBulkApprove = (ids) => {
    try {
      const toApprove = needsReview.filter((m) => ids.includes(m.id) && !m.isMultiMatch)
      setMatched((prev) => [
        ...prev,
        ...toApprove.map((m) => ({
          id: `approved-${m.id}`,
          sfName: m.sfAccount.name,
          stripeName: m.stripeCustomer.name,
          stripeEmail: m.stripeCustomer.email,
          method: 'auto',
          matchReason: m.matchReason,
          matchedDate: today,
        })),
      ])
      setNeedsReview((prev) => prev.filter((m) => !ids.includes(m.id)))
      showSuccess(`${toApprove.length} match(es) approved`)
    } catch (err) {
      console.error('Bulk approve failed:', err)
      showError(err?.message ?? 'Failed to approve')
    }
  }

  const handleLink = (sfId, stripeId, sfName, stripeName, stripeEmail) => {
    try {
      setMatched((prev) => [
        ...prev,
        {
          id: `linked-${sfId}-${stripeId}`,
          sfName,
          stripeName,
          stripeEmail: stripeEmail || '',
          method: 'admin_manual',
          matchReason: 'Admin manually linked',
          matchedDate: today,
        },
      ])
      showSuccess('Accounts linked')
    } catch (err) {
      console.error('Link failed:', err)
      showError(err?.message ?? 'Failed to link')
    }
  }

  const handleUnlink = (id) => {
    try {
      setMatched((prev) => prev.filter((m) => m.id !== id))
      showSuccess('Match unlinked')
    } catch (err) {
      console.error('Unlink failed:', err)
      showError(err?.message ?? 'Failed to unlink')
    }
  }

  const handleBulkUnlink = (ids) => {
    try {
      setMatched((prev) => prev.filter((m) => !ids.includes(m.id)))
      showSuccess(`${ids.length} match(es) unlinked`)
    } catch (err) {
      console.error('Bulk unlink failed:', err)
      showError(err?.message ?? 'Failed to unlink')
    }
  }

  const tabs = [
    { id: 'needs-review', label: 'Needs Review', count: needsReview.length, countColor: 'bg-amber-100 text-amber-700' },
    { id: 'unmatched',    label: 'Unmatched',    count: unmatchedSF.length,  countColor: 'bg-gray-100 text-gray-500' },
    { id: 'matched',      label: 'Matched',       count: matched.length,      countColor: 'bg-green-100 text-green-700' },
  ]

  return (
    <div className="min-h-full bg-gray-50 px-4 py-10">
      {/* Page header */}
      <div className="max-w-5xl mx-auto mb-6">
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <span>BillJean</span>
          <span>›</span>
          <span>Account Matching</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Account Matching</h1>
        <p className="text-sm text-gray-500 mt-1">
          Review auto-matched pairs, link unmatched accounts, and manage all confirmed connections.
        </p>
      </div>

      <div className="max-w-5xl mx-auto space-y-5">
        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Auto-confirmed', value: 147, color: 'text-green-600', bg: 'bg-green-50 border-green-100' },
            { label: 'Needs review',   value: needsReview.length, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' },
            { label: 'Unmatched',      value: unmatchedSF.length, color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200' },
          ].map(stat => (
            <div key={stat.label} className={`card p-4 border ${stat.bg}`}>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-gray-500 mt-0.5 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className={`badge text-xs ${tab.countColor}`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        <ConfirmationModal
          isOpen={rejectModalOpen}
          onClose={() => { setRejectModalOpen(false); setRejectTarget(null) }}
          onConfirm={confirmReject}
          title="Reject match(es)"
          message={
            rejectTarget?.bulk
              ? `Reject ${rejectTarget.bulk.length} selected match(es)? This cannot be undone.`
              : 'Reject this match? This cannot be undone.'
          }
          confirmText="Reject"
          type="danger"
        />

        {/* Tab content */}
        <div>
          {loading ? (
            <LoadingSpinner fullScreen text="Loading matching..." />
          ) : error ? (
            <ErrorState message={error} onRetry={() => setError(null)} />
          ) : activeTab === 'needs-review' ? (
            <NeedsReviewTab
              matches={needsReview}
              onApprove={handleApprove}
              onReject={requestReject}
              onBulkApprove={handleBulkApprove}
              onBulkReject={requestBulkReject}
            />
          ) : activeTab === 'unmatched' ? (
            <UnmatchedTab
              sfAccounts={unmatchedSF}
              stripeCustomers={unmatchedStripe}
              onLink={handleLink}
            />
          ) : (
            <MatchedTab
              matches={matched}
              onUnlink={handleUnlink}
              onBulkUnlink={handleBulkUnlink}
            />
          )}
        </div>
      </div>
    </div>
  )
}
